/* MobileSwipeMenu.tsx */
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'next-i18next'
import { EmtTypography } from 'emt-components-v2'

export type MobileSwipeMenuProps = {
  title?: string
  subtitle?: string
  component?: React.ReactNode
  children: React.ReactNode
  isLoading?: boolean
  openColor?: string
  onStateChange?: (isOpen: boolean) => void
}

export type MobileSwipeMenuRef = {
  open: () => void
  close: () => void
}

/**
 * TouchBar styled component.
 * This area acts as the “handle” to toggle open/close the swipe menu.
 */
const TouchBar = styled(Box)(() => ({
  position: 'relative',
  // height: '90px',
  paddingBottom: '10px',
  cursor: 'pointer',
  userSelect: 'none',
  // '& *': {
  //   pointerEvents: 'none', // Make all children unclickable
  // },
  '& .touch-bar': {
    position: 'absolute',
    left: '50%',
    top: '15px',
    transform: 'translateX(-50%)',
    width: '155px',
    height: '8px',
    backgroundColor: '#D2D1D4',
    borderRadius: '30px',
  },
}))

/**
 * CardsWrapper styled component.
 * It holds the entire swipe menu and adjusts its height based on open/closed state.
 */
const CardsWrapper = styled(Box)<{ open: boolean; openColor?: string }>(({ open, openColor }) => ({
  borderTopLeftRadius: '18px',
  borderTopRightRadius: '18px',
  bottom: '0px',
  position: 'fixed',
  background: open ? openColor || 'white' : 'white',
  width: '100%',
  transition: 'linear 0.15s all',
  // The scrollable inner Box will handle the scrolling,
  // so we keep overflowY hidden on the outer container.
  overflowY: 'hidden',
  zIndex: 1200,
  boxShadow: '0px 4px 56px 0px #00000014, 0px 12px 16px 0px #00000014',
}))

/**
 * Overlay styled component.
 * This semi-transparent black box covers the entire screen when the menu is open,
 * providing a dimming effect for the background content.
 */
const Overlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1199,
  pointerEvents: 'auto',
  touchAction: 'none',
}))

/** Utilities: robust body scroll lock for iOS Safari
 *  Technique: set body to position: fixed and compensate scroll via top=-scrollY,
 *  then restore scroll position + original styles on unlock.
 */
function useBodyScrollLock(isLocked: boolean) {
  const lockedRef = useRef(false)
  const scrollYRef = useRef(0)
  const prevStylesRef = useRef<Partial<CSSStyleDeclaration>>({})

  // Always provide a cleanup on unmount to prevent "stuck" body
  useEffect(() => {
    return () => {
      if (lockedRef.current) {
        unlock()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLocked && !lockedRef.current) {
      lock()
    } else if (!isLocked && lockedRef.current) {
      unlock()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked])

  const lock = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const body = document.body
    const html = document.documentElement

    // Memorize current styles we will override
    prevStylesRef.current = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      touchAction: body.style.touchAction,
      overscrollBehaviorY: body.style.overscrollBehaviorY,
    }

    const prevHtmlOverscroll = (html && (html.style as any).overscrollBehaviorY) || ''
    ;(prevStylesRef.current as any)._htmlOverscrollBehaviorY = prevHtmlOverscroll

    scrollYRef.current = window.scrollY || window.pageYOffset || 0

    // Apply lock styles
    body.style.overflow = 'hidden' // desktop browsers
    body.style.position = 'fixed' // crucial for iOS
    body.style.top = `-${scrollYRef.current}px`
    body.style.width = '100%'
    // iOS doesn't honor touch-action reliably; overscrollBehavior helps prevent rubber band
    body.style.touchAction = 'none'
    body.style.overscrollBehaviorY = 'none'
    if (html) {
      html.style.overscrollBehaviorY = 'none'
    }

    lockedRef.current = true
  }

  const unlock = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const body = document.body
    const html = document.documentElement

    // Restore styles
    body.style.overflow = prevStylesRef.current.overflow || ''
    body.style.position = prevStylesRef.current.position || ''
    body.style.top = prevStylesRef.current.top || ''
    body.style.width = prevStylesRef.current.width || ''
    body.style.touchAction = prevStylesRef.current.touchAction || ''
    body.style.overscrollBehaviorY = prevStylesRef.current.overscrollBehaviorY || ''
    if (html) {
      const prevHtmlOverscroll = (prevStylesRef.current as any)._htmlOverscrollBehaviorY || ''
      html.style.overscrollBehaviorY = prevHtmlOverscroll
    }

    // Restore the scroll position after releasing fixed positioning
    const y = scrollYRef.current
    if (Number.isFinite(y)) {
      window.scrollTo(0, y)
    }

    lockedRef.current = false
  }
}

/**
 * MobileSwipeMenu Component
 *
 * Swipe-up menu with iOS-safe body scroll lock.
 */
const MobileSwipeMenu = forwardRef<MobileSwipeMenuRef, MobileSwipeMenuProps>(
  ({ title, subtitle, component, children, isLoading = false, openColor, onStateChange }, ref) => {
    const { t: tc } = useTranslation('common')
    const [activeMobileCards, setActiveMobileCards] = useState(false)

    useImperativeHandle(ref, () => ({
      open: () => setActiveMobileCards(true),
      close: () => setActiveMobileCards(false),
    }))

    const [isTouchBarActive, setIsTouchBarActive] = useState(false)

    // Robust iOS-friendly lock/unlock + guaranteed cleanup on unmount
    useBodyScrollLock(activeMobileCards)

    // Notify parent component when the menu open state changes.
    useEffect(() => {
      onStateChange?.(activeMobileCards)
    }, [activeMobileCards, onStateChange])

    // Extra safety: if page visibility changes while locked, ensure consistency on return
    useEffect(() => {
      const onVisibility = () => {
        // Nothing to do directly here; the hook above holds the source of truth.
        // Having this listener has helped avoid rare iOS focus/visibility edge-cases.
      }
      document.addEventListener('visibilitychange', onVisibility)
      return () => document.removeEventListener('visibilitychange', onVisibility)
    }, [])

    const toggleMenu = () => setActiveMobileCards((prev) => !prev)
    return (
      <>
        {activeMobileCards && <Overlay onClick={toggleMenu} />}
        <CardsWrapper
          id="mobile-card-wrapper"
          open={activeMobileCards}
          openColor={openColor}
          sx={{
            minHeight: activeMobileCards ? '83vh' : '90px',
            height: activeMobileCards ? '83vh' : 'unset',
          }}
          className="EmtReactNativeWebViewPaddingBottom"
        >
          {/* Header/Touch Bar: Tapping here toggles the menu */}

          {component ? (
            <TouchBar>
              <Box
                className="touch-bar"
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsTouchBarActive(false)
                  toggleMenu()
                }}
                onTouchStart={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsTouchBarActive(true)
                }}
                onTouchCancel={() => setIsTouchBarActive(false)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleMenu()
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsTouchBarActive(true)
                }}
                onMouseUp={() => setIsTouchBarActive(false)}
                onMouseLeave={() => setIsTouchBarActive(false)}
              />

              <Box sx={{ pointerEvents: 'auto' }}>{!activeMobileCards ? component : null}</Box>
            </TouchBar>
          ) : (
            <TouchBar
              onTouchEnd={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsTouchBarActive(false)
                toggleMenu()
              }}
              onTouchStart={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsTouchBarActive(true)
              }}
              onTouchCancel={() => setIsTouchBarActive(false)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleMenu()
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsTouchBarActive(true)
              }}
              onMouseUp={() => setIsTouchBarActive(false)}
              onMouseLeave={() => setIsTouchBarActive(false)}
            >
              <>
                <Box className="touch-bar" />
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: '35px',
                    }}
                  >
                    <Skeleton variant="text" height={24} width="60%" />
                    <Skeleton variant="text" height={24} width="80%" />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: '35px',
                    }}
                  >
                    <EmtTypography variant="h5" fontWeight={700}>
                      {title || <Skeleton variant="text" width={100} height={24} sx={{ borderRadius: '8px' }} />}
                    </EmtTypography>
                    <EmtTypography variant="caption" fontWeight={600}>
                      {activeMobileCards ? tc('close') : subtitle}
                    </EmtTypography>
                  </Box>
                )}
              </>
            </TouchBar>
          )}

          {/* Scrollable Content */}
          <Box
            sx={{
              overflowY: 'auto',
              minHeight: activeMobileCards ? '300px' : '0px',
              height: activeMobileCards ? '100%' : '0px',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              paddingBottom: activeMobileCards ? '100px' : '0px',
              overscrollBehavior: 'contain',
              pointerEvents: isTouchBarActive ? 'none' : 'auto',
              marginTop: component && activeMobileCards ? '16px' : '0',
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {children}
          </Box>
        </CardsWrapper>
      </>
    )
  },
)

MobileSwipeMenu.displayName = 'MobileSwipeMenu'
export default MobileSwipeMenu
